import React, { useEffect, useMemo, useRef, useState } from "react";
import { History, Trash2, X } from "lucide-react";
import toast from "react-hot-toast";
import { payrollOpsService, salaryStructureService } from "../../../services/api";
import { findApplicableSalaryStructure } from "../../../utils/salaryStructureMatch";
import {
  normalizeNpsConfig,
  normalizeVpfConfig,
  normalizeMealVoucherConfig,
} from "../../../utils/salaryStructureAddons";
import StructureAddonConfigModal from "../employee-management/salary-structure/StructureAddonConfigModal";
import {
  ActionFormBody,
  ActionFormFooter,
  ActionFormHeader,
  ActionFormOverlay,
  ActionFormShell,
} from "../shared/ActionFormShell";

const STATUTORY_FLAGS = [
  { key: "pfApplicable", label: "PF Applicable" },
  { key: "ceilingPf", label: "Ceiling PF" },
  { key: "epsApplicable", label: "EPS" },
  { key: "esiApplicable", label: "ESI Applicable" },
  { key: "ptApplicable", label: "PT Applicable" },
  { key: "gratuityApplicable", label: "Gratuity Applicable" },
];

const BENEFIT_FLAGS = [
  { key: "performanceBonus", label: "Performance Bonus (PB)", addonKey: "performanceBonus" },
  { key: "employerNps", label: "Employer NPS", addonKey: "nps" },
  { key: "vpf", label: "VPF", addonKey: "vpf" },
  { key: "mealVoucher", label: "Meal Voucher", addonKey: "mealVoucher" },
];

function monthInputDefault() {
  return new Date().toISOString().slice(0, 7);
}

function emptyBenefitDetails(employee) {
  const hasPb = Number(employee?.performanceBonus || employee?.performanceBonusAmount || 0) > 0;
  return {
    performanceBonus: {
      enabled: hasPb,
      amount: hasPb ? String(employee?.performanceBonus ?? employee?.performanceBonusAmount ?? "") : "",
      period: employee?.performanceBonusPeriod || "annual",
      frequency: employee?.performanceBonusFrequency || "annual",
    },
    employerNps: { enabled: false },
    vpf: { enabled: false },
    mealVoucher: { enabled: false },
  };
}

function hydrateBenefitDetails(raw, employee) {
  const base = emptyBenefitDetails(employee);
  if (!raw || typeof raw !== "object") return base;
  const merge = (key) => {
    const v = raw[key];
    if (v && typeof v === "object") {
      return { ...base[key], ...v, enabled: v.enabled === true };
    }
    return { ...base[key], enabled: !!v };
  };
  return {
    performanceBonus: merge("performanceBonus"),
    employerNps: merge("employerNps"),
    vpf: merge("vpf"),
    mealVoucher: merge("mealVoucher"),
  };
}

function isOn(entry) {
  return !!(entry && entry.enabled);
}

function flagSummary(row) {
  const parts = [
    `PF ${row.pfApplicable !== false ? "On" : "Off"}`,
    `Ceiling ${row.ceilingPf !== false ? "On" : "Off"}`,
    `EPS ${row.epsApplicable ? "On" : "Off"}`,
    `ESI ${row.esiApplicable !== false ? "On" : "Off"}`,
    `PT ${row.ptApplicable !== false ? "On" : "Off"}`,
    `Gratuity ${row.gratuityApplicable !== false ? "On" : "Off"}`,
  ];
  const b = hydrateBenefitDetails(row.benefits, null);
  const benefitParts = [
    isOn(b.performanceBonus) ? "PB" : null,
    isOn(b.employerNps) ? "NPS" : null,
    isOn(b.vpf) ? "VPF" : null,
    isOn(b.mealVoucher) ? "Meal" : null,
  ].filter(Boolean);
  if (benefitParts.length) parts.push(`Benefits: ${benefitParts.join(", ")}`);
  return parts.join(" · ");
}

/**
 * Effective-dated statutory & benefits applicability update for one employee.
 */
export default function UpdateStatutoryApplicabilityModal({
  isOpen,
  onClose,
  employee,
  structures = [],
  darkMode = false,
  onSaved,
}) {
  const [effectiveMonth, setEffectiveMonth] = useState(monthInputDefault());
  const [flags, setFlags] = useState({
    pfApplicable: true,
    ceilingPf: true,
    epsApplicable: false,
    esiApplicable: true,
    ptApplicable: true,
    gratuityApplicable: true,
  });
  const [benefitDetails, setBenefitDetails] = useState(() => emptyBenefitDetails(null));
  const [remarks, setRemarks] = useState("");
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [addonModal, setAddonModal] = useState(null); // performanceBonus | nps | vpf | mealVoucher
  const [pendingEnableKey, setPendingEnableKey] = useState(null);
  const addonSavedRef = useRef(false);

  const structure = useMemo(
    () => (employee ? findApplicableSalaryStructure(employee, structures) : null),
    [employee, structures]
  );

  const structureNps = useMemo(() => normalizeNpsConfig(structure?.nps), [structure]);
  const structureVpf = useMemo(() => normalizeVpfConfig(structure?.vpf), [structure]);
  const structureMeal = useMemo(
    () => normalizeMealVoucherConfig(structure?.mealVoucher),
    [structure]
  );
  const structureAllowsPb = !!structure?.performanceBonus?.enabled;
  const structureAllowsNps = !!structureNps.enabled;
  const structureAllowsVpf = !!structureVpf.enabled;
  const structureAllowsMeal = !!structureMeal.enabled;
  const allowedBenefitFlags = useMemo(
    () =>
      BENEFIT_FLAGS.filter((f) => {
        if (f.key === "performanceBonus") return structureAllowsPb;
        if (f.key === "employerNps") return structureAllowsNps;
        if (f.key === "vpf") return structureAllowsVpf;
        if (f.key === "mealVoucher") return structureAllowsMeal;
        return false;
      }),
    [structureAllowsPb, structureAllowsNps, structureAllowsVpf, structureAllowsMeal]
  );
  const hasBenefits = allowedBenefitFlags.length > 0;

  const gateBenefitDetails = (details) => ({
    performanceBonus: structureAllowsPb
      ? details.performanceBonus
      : { ...(details.performanceBonus || {}), enabled: false },
    employerNps: structureAllowsNps
      ? details.employerNps
      : { ...(details.employerNps || {}), enabled: false },
    vpf: structureAllowsVpf
      ? details.vpf
      : { ...(details.vpf || {}), enabled: false },
    mealVoucher: structureAllowsMeal
      ? details.mealVoucher
      : { ...(details.mealVoucher || {}), enabled: false },
  });

  const loadHistory = async () => {
    if (!employee?.id) return [];
    try {
      const res = await payrollOpsService.listStatutoryApplicability(employee.id);
      const rows = Array.isArray(res?.data) ? res.data : [];
      setHistory(rows);
      return rows;
    } catch {
      setHistory([]);
      return [];
    }
  };

  useEffect(() => {
    if (!isOpen || !employee?.id) return;
    setEffectiveMonth(monthInputDefault());
    setFlags({
      pfApplicable: employee.pfApplicable !== false,
      ceilingPf: employee.ceilingPf !== false,
      epsApplicable: employee.epsApplicable === true,
      esiApplicable: employee.esiApplicable !== false,
      ptApplicable: employee.ptApplicable !== false,
      gratuityApplicable: employee.gratuityApplicable !== false,
    });
    setBenefitDetails(gateBenefitDetails(emptyBenefitDetails(employee)));
    setRemarks("");
    setShowHistory(false);
    setAddonModal(null);
    setPendingEnableKey(null);
    addonSavedRef.current = false;
    Promise.all([
      loadHistory(),
      salaryStructureService.getActiveStructure(employee.id).catch(() => null),
    ]).then(([rows, activeRes]) => {
      const latest = rows[0];
      if (latest) {
        setFlags({
          pfApplicable: latest.pfApplicable !== false,
          ceilingPf: latest.ceilingPf !== false,
          epsApplicable: latest.epsApplicable === true,
          esiApplicable: latest.esiApplicable !== false,
          ptApplicable: latest.ptApplicable !== false,
          gratuityApplicable: latest.gratuityApplicable !== false,
        });
      }
      // NPS / VPF / Meal not recorded in history → the saved structure opt-in is what payroll uses.
      let params = activeRes?.data?.parametersJson || {};
      if (typeof params === "string") {
        try {
          params = JSON.parse(params);
        } catch {
          params = {};
        }
      }
      const addons = params.structureAddons || params;
      const fromStructure = Object.fromEntries(
        Object.entries({
          employerNps: addons.employeeNps,
          vpf: addons.employeeVpf,
          mealVoucher: addons.employeeMealVoucher || addons.employeeMeal,
        }).filter(([, v]) => v && typeof v === "object")
      );
      const recorded = latest?.benefits || {};
      if (latest?.benefits || Object.keys(fromStructure).length) {
        setBenefitDetails(
          gateBenefitDetails(
            hydrateBenefitDetails(
              {
                performanceBonus: emptyBenefitDetails(employee).performanceBonus,
                ...fromStructure,
                ...recorded,
              },
              employee
            )
          )
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, employee, structureAllowsPb, structureAllowsNps, structureAllowsVpf, structureAllowsMeal]);

  if (!isOpen || !employee) return null;

  const openConfigure = (benefitKey) => {
    const meta = BENEFIT_FLAGS.find((f) => f.key === benefitKey);
    if (!meta) return;
    setAddonModal(meta.addonKey);
  };

  const handleBenefitToggle = (benefitKey, checked) => {
    const allowed = allowedBenefitFlags.some((f) => f.key === benefitKey);
    if (!allowed) {
      toast.error("This benefit is not enabled on the employee's salary structure");
      return;
    }
    if (!checked) {
      setBenefitDetails((prev) => ({
        ...prev,
        [benefitKey]: { ...(prev[benefitKey] || {}), enabled: false },
      }));
      return;
    }
    if (benefitKey === "vpf" && flags.pfApplicable === false) {
      toast.error("VPF requires PF Applicable");
      return;
    }
    if (benefitKey === "employerNps") {
      const pran = String(employee?.pranNumber || "").replace(/\D/g, "");
      if (!/^\d{12}$/.test(pran)) {
        toast.error(
          "Add a valid 12-digit PRAN under Employee → Statutory → NPS / PRAN before enabling Employer NPS."
        );
        return;
      }
    }
    setPendingEnableKey(benefitKey);
    openConfigure(benefitKey);
  };

  const handleAddonSave = (payload) => {
    const key =
      addonModal === "performanceBonus"
        ? "performanceBonus"
        : addonModal === "nps"
          ? "employerNps"
          : addonModal === "vpf"
            ? "vpf"
            : "mealVoucher";
    addonSavedRef.current = true;
    setBenefitDetails((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        ...payload,
        enabled: true,
      },
    }));
    setPendingEnableKey(null);
  };

  const handleAddonClose = () => {
    if (!addonSavedRef.current && pendingEnableKey) {
      const key = pendingEnableKey;
      setBenefitDetails((prev) => ({
        ...prev,
        [key]: { ...(prev[key] || {}), enabled: false },
      }));
    }
    addonSavedRef.current = false;
    setPendingEnableKey(null);
    setAddonModal(null);
  };

  const handleSave = async () => {
    if (!effectiveMonth) {
      toast.error("Select effective month");
      return;
    }
    if (structureAllowsPb && isOn(benefitDetails.performanceBonus)) {
      const amt = Number(benefitDetails.performanceBonus.amount);
      if (!Number.isFinite(amt) || amt <= 0) {
        toast.error("Configure Performance Bonus amount before saving");
        openConfigure("performanceBonus");
        return;
      }
    }
    if (structureAllowsNps && isOn(benefitDetails.employerNps)) {
      const pran = String(employee?.pranNumber || "").replace(/\D/g, "");
      if (!/^\d{12}$/.test(pran)) {
        toast.error(
          "PRAN (12 digits) is mandatory for Employer NPS. Update it under Employee → Statutory → NPS / PRAN, then try again."
        );
        return;
      }
    }
    if (structureAllowsMeal && isOn(benefitDetails.mealVoucher)) {
      if (structureMeal.rateMode === "employee") {
        const amt = Number(
          benefitDetails.mealVoucher?.monthlyAmount ??
            benefitDetails.mealVoucher?.flatMonthlyAmount ??
            benefitDetails.mealVoucher?.amount
        );
        const perMeal = Number(benefitDetails.mealVoucher?.amountPerMeal) || 0;
        const meals = Number(benefitDetails.mealVoucher?.mealsPerDay) || 0;
        const hasDerived = perMeal > 0 && meals > 0;
        if ((!Number.isFinite(amt) || amt <= 0) && !hasDerived) {
          toast.error(
            "Configure Meal Voucher monthly amount (or Amount/meal × Meals/day) before saving"
          );
          openConfigure("mealVoucher");
          return;
        }
      } else if (!(Number(structureMeal.flatMonthlyAmount) > 0)) {
        toast.error(
          "Structure Meal Voucher flat amount is zero. Set a flat monthly amount on the salary structure, or switch Meal to employee-level."
        );
        return;
      }
    }
    const benefitsToSave = {
      performanceBonus: structureAllowsPb
        ? benefitDetails.performanceBonus
        : { enabled: false },
      employerNps: structureAllowsNps ? benefitDetails.employerNps : { enabled: false },
      vpf: structureAllowsVpf ? benefitDetails.vpf : { enabled: false },
      mealVoucher: structureAllowsMeal ? benefitDetails.mealVoucher : { enabled: false },
    };
    setSaving(true);
    try {
      await payrollOpsService.createStatutoryApplicability(employee.id, {
        effectiveFrom: effectiveMonth,
        ...flags,
        benefits: benefitsToSave,
        remarks: remarks || null,
      });
      toast.success("Statutory & benefits applicability updated");
      onSaved?.({ employeeId: employee.id, effectiveMonth, flags, benefits: benefitsToSave });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!row?.canDelete) {
      toast.error(row?.deleteBlockReason || "Only the latest update can be deleted");
      return;
    }
    if (
      !window.confirm(
        `Delete update from ${String(row.effectiveFrom).slice(0, 7)}? The previous stored update will become active.`
      )
    ) {
      return;
    }
    setDeletingId(row.id);
    try {
      const res = await payrollOpsService.deleteStatutoryApplicability(employee.id, row.id);
      toast.success(res?.message || "Update deleted");
      const rows = await loadHistory();
      const restored = rows[0];
      if (restored) {
        setFlags({
          pfApplicable: restored.pfApplicable !== false,
          ceilingPf: restored.ceilingPf !== false,
          epsApplicable: restored.epsApplicable === true,
          esiApplicable: restored.esiApplicable !== false,
          ptApplicable: restored.ptApplicable !== false,
          gratuityApplicable: restored.gratuityApplicable !== false,
        });
        if (restored.benefits) {
          setBenefitDetails(hydrateBenefitDetails(restored.benefits, employee));
        }
      }
      onSaved?.({ employeeId: employee.id, deleted: true });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const panel = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]";

  const checkLabelClass = "inline-flex items-center gap-2 text-sm cursor-pointer";

  const addonInitial =
    addonModal === "performanceBonus"
      ? benefitDetails.performanceBonus
      : addonModal === "nps"
        ? benefitDetails.employerNps
        : addonModal === "vpf"
          ? benefitDetails.vpf
          : benefitDetails.mealVoucher;

  return (
    <>
    <ActionFormOverlay onClose={onClose}>
      <ActionFormShell className={panel}>
        <ActionFormHeader
          title="Update Statutory & Benefits Applicability"
          subtitle={`${[employee.firstName, employee.lastName].filter(Boolean).join(" ")}${
            employee.employeeCode || employee.empCode
              ? ` (${employee.employeeCode || employee.empCode})`
              : ""
          } · Changes apply from the selected month.`}
          onClose={onClose}
        />

        <ActionFormBody className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium">Effective from (month)</label>
            <input
              type="month"
              value={effectiveMonth}
              onChange={(e) => setEffectiveMonth(e.target.value)}
              className={`w-full rounded border px-3 py-2 text-sm ${
                darkMode
                  ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                  : "border-[hsl(var(--ds-border))]"
              }`}
            />
          </div>

          <div className="rounded-lg border border-[hsl(var(--ds-border))] p-3">
            <h4 className="mb-2 text-sm font-semibold">Statutory & Benefits Applicability</h4>
            <div
              className={
                hasBenefits
                  ? "grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-0 sm:divide-x sm:divide-[hsl(var(--ds-border))]"
                  : ""
              }
            >
              <div
                className={
                  hasBenefits
                    ? "grid grid-cols-1 gap-2 sm:grid-cols-2 sm:pr-3"
                    : "grid grid-cols-1 gap-2 sm:grid-cols-2"
                }
              >
                {STATUTORY_FLAGS.map((f) => (
                  <label key={f.key} className={checkLabelClass}>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded"
                      checked={!!flags[f.key]}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFlags((prev) => {
                          const next = { ...prev, [f.key]: checked };
                          if (f.key === "pfApplicable" && !checked) {
                            next.ceilingPf = true;
                            if (isOn(benefitDetails.vpf)) {
                              setBenefitDetails((b) => ({
                                ...b,
                                vpf: { ...b.vpf, enabled: false },
                              }));
                              toast("VPF turned off because PF is not applicable", { icon: "ℹ️" });
                            }
                          }
                          return next;
                        });
                      }}
                    />
                    <span>{f.label}</span>
                  </label>
                ))}
              </div>
              {hasBenefits ? (
                <div className="space-y-2 sm:pl-3">
                  {allowedBenefitFlags.map((f) => (
                    <div key={f.key}>
                      <label className={checkLabelClass}>
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded"
                          checked={isOn(benefitDetails[f.key])}
                          disabled={f.key === "vpf" && flags.pfApplicable === false}
                          onChange={(e) => handleBenefitToggle(f.key, e.target.checked)}
                        />
                        <span>{f.label}</span>
                      </label>
                      {isOn(benefitDetails[f.key]) ? (
                        <button
                          type="button"
                          className="ml-6 mt-0.5 block text-xs font-medium text-[hsl(var(--ds-primary))] hover:underline"
                          onClick={() => openConfigure(f.key)}
                        >
                          Configure…
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
            {!structure ? (
              <p className="mt-2 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                No matched salary structure — benefits stay hidden until a structure with add-ons is
                assigned.
              </p>
            ) : !hasBenefits ? (
              <p className="mt-2 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                Benefits appear here only when enabled on the assigned salary structure (PB / NPS /
                VPF / Meal).
              </p>
            ) : null}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium">Remarks</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
              className={`w-full rounded border px-3 py-2 text-sm ${
                darkMode
                  ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                  : "border-[hsl(var(--ds-border))]"
              }`}
              placeholder="e.g. Remove PF ceiling from Jul-2026 per employee request"
            />
          </div>
        </ActionFormBody>

        <ActionFormFooter className="justify-between">
          <button
            type="button"
            onClick={() => setShowHistory(true)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm ${
              darkMode ? "bg-[hsl(var(--ds-muted))]" : "border border-[hsl(var(--ds-border))]"
            }`}
          >
            <History size={16} strokeWidth={1.75} />
            History{history.length ? ` (${history.length})` : ""}
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className={`rounded-lg px-4 py-2 text-sm ${
                darkMode ? "bg-[hsl(var(--ds-muted))]" : "border border-[hsl(var(--ds-border))]"
              }`}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={handleSave}
              className="rounded-lg bg-[hsl(var(--ds-primary))] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save applicability"}
            </button>
          </div>
        </ActionFormFooter>
      </ActionFormShell>
    </ActionFormOverlay>

      <StructureAddonConfigModal
        isOpen={addonModal === "performanceBonus"}
        onClose={handleAddonClose}
        title="Configure Performance Bonus"
        addonKey="performanceBonus"
        level="employee"
        initialConfig={addonInitial || {}}
        onSave={handleAddonSave}
      />
      <StructureAddonConfigModal
        isOpen={addonModal === "nps"}
        onClose={handleAddonClose}
        title="Configure Employer NPS"
        addonKey="nps"
        level="employee"
        initialConfig={addonInitial || {}}
        structureDefaults={structureNps}
        onSave={handleAddonSave}
      />
      <StructureAddonConfigModal
        isOpen={addonModal === "vpf"}
        onClose={handleAddonClose}
        title="Configure VPF"
        addonKey="vpf"
        level="employee"
        initialConfig={addonInitial || {}}
        structureDefaults={structureVpf}
        pfApplicable={flags.pfApplicable !== false}
        onSave={handleAddonSave}
      />
      <StructureAddonConfigModal
        isOpen={addonModal === "mealVoucher"}
        onClose={handleAddonClose}
        title="Configure Meal Voucher"
        addonKey="mealVoucher"
        level="employee"
        initialConfig={addonInitial || {}}
        structureDefaults={structureMeal}
        onSave={handleAddonSave}
      />

      {showHistory ? (
        <div
          className="fixed inset-0 z-[10030] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowHistory(false)}
        >
          <div
            className={`flex max-h-[80vh] w-[min(100%,560px)] flex-col overflow-hidden rounded-xl border shadow-2xl ${panel}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-4 py-3">
              <h4 className="text-sm font-semibold">Applicability History</h4>
              <button
                type="button"
                onClick={() => setShowHistory(false)}
                className="rounded-full p-1 opacity-70 hover:opacity-100"
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {history.length === 0 ? (
                <p className="text-sm text-[hsl(var(--ds-muted-fg))]">No updates yet.</p>
              ) : (
                <ul className="space-y-2">
                  {history.map((h) => (
                    <li
                      key={h.id}
                      className="flex items-start justify-between gap-3 rounded-lg border border-[hsl(var(--ds-border))] px-3 py-2"
                    >
                      <div className="min-w-0 text-xs">
                        <div className="text-sm font-medium">
                          From {String(h.effectiveFrom).slice(0, 7)}
                          {h.isLatest ? (
                            <span className="ml-2 text-[10px] font-semibold text-[hsl(var(--ds-primary))]">
                              Active
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-0.5 text-[hsl(var(--ds-muted-fg))]">{flagSummary(h)}</div>
                        {h.remarks ? (
                          <div className="mt-0.5 text-[hsl(var(--ds-muted-fg))]">{h.remarks}</div>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        disabled={!h.canDelete || deletingId === h.id}
                        title={
                          h.canDelete
                            ? "Delete and restore previous"
                            : h.deleteBlockReason || "Cannot delete"
                        }
                        onClick={() => handleDelete(h)}
                        className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <Trash2 size={14} strokeWidth={1.75} />
                        {deletingId === h.id ? "…" : "Delete"}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
